import React, { useState } from 'react';
import { Card, Button, Modal, Form, Alert, Spinner, Badge, Container } from 'react-bootstrap';
import Navbar from "./navbar";
import Contact from "./Contact";
import Footer from "./Footer";
import { useUserQueries, updateQuery, deleteQuery } from '../hooks/useQueries';

const MyQueries = () => {
  const { queries, loading, error, refetch } = useUserQueries();
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [editText, setEditText] = useState('');
  const [editLawyerType, setEditLawyerType] = useState('General');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const lawyerTypes = [
    'General',
    'Family Lawyer',
    'Criminal Defense Lawyer',
    'Business Lawyer',
    'Estate Planning Lawyer',
    'Civil Rights Lawyer',
    'Tax Lawyer',
    'Immigration Lawyer'
  ];

  const handleEditClick = (query) => {
    setSelectedQuery(query);
    setEditText(query.query);
    setEditLawyerType(query.lawyerType || 'General');
    setShowEditModal(true);
    setSubmitError('');
  };

  const handleUpdateQuery = async (e) => {
    e.preventDefault();
    
    if (!editText.trim()) {
      setSubmitError('Please enter your query');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const result = await updateQuery(selectedQuery.id, editText, editLawyerType);

    setSubmitting(false);

    if (result.success) {
      setShowEditModal(false);
      refetch();
    } else {
      setSubmitError(result.error);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    if (window.confirm('Are you sure you want to delete this query?')) {
      const result = await deleteQuery(queryId);
      if (result.success) {
        refetch();
      } else {
        alert('Error deleting query: ' + result.error);
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <main className="main-content">
        <Container className="py-4">
          <div className="section-header">
            <h1>My Legal Queries</h1>
          </div>

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading queries...</p>
            </div>
          )}

          {error && (
            <Alert variant="danger">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {!loading && !error && queries.length === 0 && (
            <Alert variant="info">
              You haven't submitted any queries yet. Go to "Ask Lawyer" to submit your first query!
            </Alert>
          )}

          {queries.map((query) => (
            <Card key={query.id} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <Badge bg="info" className="me-2">{query.lawyerType || 'General'}</Badge>
                    <Badge bg={query.status === 'answered' ? 'success' : 'warning'}>
                      {query.status || 'pending'}
                    </Badge>
                  </div>
                  <small style={{ color: '#8b7355' }}>
                    {query.createdAt?.toDate().toLocaleString()}
                  </small>
                </div>

                <h5 style={{ color: '#c9a961', marginBottom: '1rem' }}>Your Query:</h5>
                <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>
                  {query.query}
                </p>

                {query.response && (
                  <>
                    <hr />
                    <h5 style={{ color: '#c9a961', marginTop: '1.5rem', marginBottom: '1rem' }}>
                      AI Lawyer Response:
                    </h5>
                    <div style={{ 
                      backgroundColor: 'rgba(201, 169, 97, 0.1)', 
                      padding: '1rem', 
                      borderRadius: '4px',
                      borderLeft: '3px solid #c9a961',
                      lineHeight: '1.7'
                    }}>
                      {query.response}
                    </div>
                  </>
                )}

                <div className="mt-3">
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEditClick(query)}
                  >
                    Edit Query
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteQuery(query.id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </Container>

        {/* Edit Query Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Query</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <Alert variant="danger">{submitError}</Alert>}
            <Form onSubmit={handleUpdateQuery}>
              <Form.Group className="mb-3">
                <Form.Label>Lawyer Type</Form.Label>
                <Form.Select
                  value={editLawyerType}
                  onChange={(e) => setEditLawyerType(e.target.value)}
                  disabled={submitting}
                >
                  {lawyerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Query Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>

              <Alert variant="info">
                Note: Editing your query will generate a new AI response.
              </Alert>

              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Query'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default MyQueries;
